import { AxiosPromise } from 'axios';
import { ProductRepaymentTypes, ProductType } from 'domain/Product';
import { AssurancePolicy } from 'modules/cm/api/cases/assurancePolicies/types';
import { FuturePensionDefinedContribution } from 'modules/cm/api/cases/definedContributions/types';
import { FuturePensionAnnuity } from 'modules/cm/api/cases/futureAnnuity/types';
import { CurrentIncome } from 'modules/cm/api/cases/income/types';
import { Product as APIProduct } from 'modules/cm/api/cases/products/types';
import { RepaymentStrategy } from 'modules/cm/api/cases/repaymentStrategy/RepaymentStrategy';
import { CaseError as DomainCaseError } from 'modules/cm/domain/Case/Error';
import { DueDiligenceStatus } from 'modules/cm/domain/DueDiligence/DueDiligence';
import { Nullable } from 'utils/types';
import { PepperReviewSummary } from './PepperReview/types';
import { ApplicantAddress } from './addresses/types';
import { Asset } from './assets/types';
import { CaseError as APICaseError } from './caseError/caseError';
import { FuturePensionDefinedBenefit } from './definedBenefits/types';
import { Expenditure } from './liabilities/types';
import { Underwriting, UnderwritingSummary } from './underwiting/types';

type PowerOfAttorney = {
  id: number;
  name: Nullable<string>;
  phoneNumber: Nullable<string>;
  email: Nullable<string>;
  postcode: Nullable<string>;
  flatNumber: Nullable<string>;
  buildingName: Nullable<string>;
  buildingNumber: Nullable<string>;
  street: Nullable<string>;
  city: Nullable<string>;
};

type Applicant = CurrentIncome & {
  id: number;
  title: string;
  firstName: string;
  lastName: string;
  expenditures: Expenditure[];
  commitmentsMonthlyAmountToBeCleared: number;
  commitmentsBalanceToBeCleared: number;
  commitmentsMonthlyAmountRemaining: number;
  commitmentsBalanceRemaining: number;
  assets: Asset[];
  middleName: Nullable<string>;
  previousName: Nullable<string>;
  proofOfId: Nullable<string>;
  dateOfBirth: Nullable<string>;
  age: Nullable<number>;
  plannedRetirementAge: Nullable<number>;
  gender: Nullable<string>;
  maritalStatus: Nullable<string>;
  addresses: ApplicantAddress[];
  nationality: Nullable<string>;
  isUkResident: Nullable<boolean>;
  hasIndefiniteLeaveToRemain: Nullable<boolean>;
  phoneHome: Nullable<string>;
  isPhoneHomePepper: Nullable<boolean>;
  phoneMobile: Nullable<string>;
  isPhoneMobilePepper: Nullable<boolean>;
  phoneWork: Nullable<string>;
  isPhoneWorkPepper: Nullable<boolean>;
  email: Nullable<string>;
  isEmailPepper: Nullable<boolean>;
  additionalEmail: Nullable<string>;
  isAdditionalEmailPepper: Nullable<boolean>;
  isCriminal: Nullable<boolean>;
  criminalConvictionsDetails: Nullable<string>;
  hasAdverseCredit: Nullable<boolean>;
  adverseCreditDetails: Nullable<string>;
  bankruptcyAmount: Nullable<number>;
  bankruptcyLatestDate: Nullable<string>;
  ccjAmount: Nullable<number>;
  ccjLatestDate: Nullable<string>;
  defaultAmount: Nullable<number>;
  defaultLatestDate: Nullable<string>;
  mortgageArrearsAmount: Nullable<number>;
  mortgageArrearsLatestDate: Nullable<string>;
  powerOfAttorney: PowerOfAttorney;
  currentStatePension: Nullable<number>;
  currentDbNameOfScheme: Nullable<string>;
  currentDbAnnualIncome: Nullable<number>;
  currentDbPercentPassedOnDeath: Nullable<number>;
  currentDcNameOfScheme: Nullable<string>;
  currentDcCurrentValue: Nullable<number>;
  currentDcPercentPassedOnDeath: Nullable<number>;
  currentDcAnnualIncomeDrawn: Nullable<number>;
  currentAnnuityNameOfAnnuityProvider: Nullable<string>;
  currentAnnuityGrossAnnualIncome: Nullable<number>;
  currentAnnuityPercentPassedOnDeath: Nullable<number>;
  futureStatePension: Nullable<number>;
  futurePensionDefinedBenefits: FuturePensionDefinedBenefit[];
  futurePensionDefinedContributions: FuturePensionDefinedContribution[];
  futurePensionAnnuity: FuturePensionAnnuity[];
  employmentStatus: Nullable<string>;
  employedJobTitle: Nullable<string>;
  employedCompanyName: Nullable<string>;
  employedCompanyAddress: Nullable<string>;
  employedNature: Nullable<string>;
  employedStartDate: Nullable<string>;
  employedAnticipatedAge: Nullable<number>;
  selfEmployedJobTitle: Nullable<string>;
  selfEmployedCompanyName: Nullable<string>;
  selfEmployedCompanyAddress: Nullable<string>;
  selfEmployedNature: Nullable<string>;
  selfEmployedStartDate: Nullable<string>;
  businessType: Nullable<string>;
  selfEmployedAnticipatedAge: Nullable<number>;
  minimumRetiredIncome: Nullable<number>;
  futureIncomePensionIncome: Nullable<number>;
  futureIncomeTotalIncome: Nullable<number>;
  employedAnticipatedGrossAnnualIncome: Nullable<number>;
  employedIncomeDetails: Nullable<string>;
  selfEmployedAnticipatedGrossAnnualIncome: Nullable<number>;
  selfEmployedIncomeDetails: Nullable<string>;
  minimumTaxableRetiredIncome: Nullable<number>;
  hasTaxableRetiredIncomeContinuing: boolean;
  minimumTaxFreeRetiredIncome: Nullable<number>;
  hasTaxFreeRetiredIncomeContinuing: boolean;
  numberOfDependants: Nullable<number>;
  otherIncomeAnticipatedGrossAnnualIncome: Nullable<number>;
  otherIncomeDetails: Nullable<string>;
  minimumTaxableRetiredIncomeDetails: Nullable<string>;
  minimumTaxFreeRetiredIncomeDetails: Nullable<string>;
  lumpSumDrawdown: Nullable<number>;
  repaymentLumpSum: Nullable<number>;
  hasCustomisedServices: Nullable<boolean>;
  customisedServicesRequest: Nullable<string>;
};

type PropertyOccupant = {
  id: number;
  fullName: Nullable<string>;
  dateOfBirth: Nullable<string>;
};

type Resource = {
  option: string;
  value: string;
};

type Property = {
  flatNumber: Nullable<string>;
  buildingName: Nullable<string>;
  buildingNumber: Nullable<string>;
  street: Nullable<string>;
  city: Nullable<string>;
  postcode: Nullable<string>;

  hasOccupantsOver17: Nullable<string>;
  occupants: PropertyOccupant[];

  type: Nullable<string>;
  isPrimaryHome: Nullable<boolean>;
  floorNumber: Nullable<number>;
  numberOfFloorsInTheBuilding: Nullable<number>;
  hasLiftAccess: Nullable<boolean>;
  tenureType: Nullable<string>;
  remainingYears: Nullable<number>;
  isExLocalAuthority: Nullable<boolean>;
  isRetirementHome: Nullable<boolean>;
  isRestrictedCovenant: Nullable<boolean>;
  isAboveCommercialPremises: Nullable<boolean>;
  commercialPremisesDetails: Nullable<string>;
  hasCommercialUsageAbove25Percent: Nullable<boolean>;
  commercialUsageDetails: Nullable<string>;
  numberOfKitchens: Nullable<number>;
  numberOfBedrooms: Nullable<number>;
  hasAgriculturalRestrictions: Nullable<string>;
  propertyYearBuilt: Nullable<number>;
  warrantyProvider: Nullable<string>;
  isPropertyNewBuild: Nullable<boolean>;
  propertyConstruction: Nullable<string>;
  propertyDetails: Nullable<string>;
  instructionDate: Nullable<string>;
  inspectionDate: Nullable<string>;
  valuationType: Nullable<string>;
  hasSpecialistReport: Nullable<boolean>;
  titleNumber: Nullable<string>;
  estimatedPropertyValue: Nullable<number>;
  propertyPurchasePrice: Nullable<number>;
  actualPropertyValue: Nullable<number>;
};

type Conveyancing = {
  solicitorName: Nullable<string>;
  solicitorEmail: Nullable<string>;
  solicitorPostcode: Nullable<string>;
  solicitorFlatNumber: Nullable<string>;
  solicitorBuildingNumber: Nullable<string>;
  solicitorBuildingName: Nullable<string>;
  solicitorStreet: Nullable<string>;
  solicitorCity: Nullable<string>;
  solicitorContactNumber: Nullable<string>;
};

type Admin = {
  offerDate: Nullable<string>;
  cotRecDate: Nullable<string>;
  fundsReqDate: Nullable<string>;
  expectedCompletionDate: Nullable<string>;
  completionDate: Nullable<string>;
  pepperUploadDate: Nullable<string>;
  pepperAccountId: Nullable<string>;
  declineDate: Nullable<string>;
  swapCost: Nullable<number>;
  expectedMaturity: Nullable<number>;
};

export type AffordabilityResults = {
  afStage: Nullable<string>;
  afDisposableIncome: Nullable<string>;
  afDisposableIncomeBuffer: Nullable<string>;
  afLivingCosts: Nullable<string>;
  afNetCashflow: Nullable<string>;
  afMaxLoanPossible: Nullable<string>;
};

export interface Product {
  productCode: Nullable<string>;
  pepperProductName: Nullable<string>;
  productFixedTerm: Nullable<number>;
  productFeePaymentMethod: Nullable<string>;
  productFixedInterestRate: Nullable<number>;
  productReversionRate: Nullable<number>;
  productTermLength: Nullable<number>;
  productFee: Nullable<number>;
  productType: Nullable<ProductType>;
  productTier: Nullable<string>;
  productRepaymentType: Nullable<ProductRepaymentTypes>;
}

export type CaseResponse = AxiosPromise<CaseResponseData>;

export type CaseData = {
  caseId: string;
  applicant1: Applicant;
  applicant2: Nullable<Applicant>;
  applicationType: Nullable<string>;
  accountName: Nullable<string>;
  accountNumber: Nullable<string>;
  actualPropertyValue: Nullable<number>;
  bankOrBuildingSocietyName: Nullable<string>;
  createdBy: Nullable<number>;
  creditPolicyExemptionRationale: Nullable<string>;
  currentMortgageProvider: Nullable<string>;
  dateSubmitted: Nullable<string>;
  debtToIncome: Nullable<number>;
  delphiScore: Nullable<number>;
  estimatedPropertyValue: Nullable<number>;
  id: Nullable<number>;
  isBuyToLet: Nullable<boolean>;
  isOnHoldStatus: Nullable<boolean>;
  isApprovedWithAuthorityLevels: Nullable<boolean>;
  isPreOfferCarriedOutForApplicants: Nullable<boolean>;
  hasAdverseCredit: Nullable<boolean>;
  isCreditPolicyExemption: Nullable<boolean>;
  isFirstTimeBuyer: Nullable<boolean>;
  isPrivateSale: Nullable<boolean>;
  loanAmountPurchase: Nullable<number>;
  loanAmountRemortgage: Nullable<number>;
  loanAssistingOthers: Nullable<number>;
  loanDebtConsolidation: Nullable<number>;
  loanDiscretionarySpend: Nullable<number>;
  loanHomeImprovements: Nullable<number>;
  monthlyBasicSpend: Nullable<number>;
  numberOfApplicants: Nullable<number>;
  pepperPurposeOfTheLoan: Nullable<string>;
  originatorId: Nullable<string>;
  postcode: Nullable<string>;
  propertyPurchasePrice: Nullable<number>;
  purposeOfCapitalRaise: Nullable<string>;
  purposeOfTheLoan: Nullable<string>;
  sortCode: Nullable<string>;
  sourceOfDeposit: Nullable<string>;
  stage: Nullable<string>;
  typeOfApplication: Nullable<string>;
  updatedAt: Nullable<string>;
  valuationFeePayment: Nullable<string>;
  grossLoan: Nullable<number>;
  netLoan: Nullable<number>;
  ltv: Nullable<number>;
  valuationFeeAmount: Nullable<number>;
  reinspectionFee: Nullable<number>;
  depositSourceBuilders: Nullable<boolean>;
  depositSourceDetails: Nullable<string>;
  depositSourceEquityFromSale: Nullable<boolean>;
  depositSourceGifts: Nullable<boolean>;
  depositSourceOther: Nullable<boolean>;
  depositSourceSavings: Nullable<boolean>;
  adviserName: Nullable<string>;
  adviserFee: Nullable<number>;
  adviserPhone: Nullable<string>;
  adviserEmail: Nullable<string>;
  standardCommissionTerms: Nullable<string>;
  enhancedCommissionTerms: Nullable<string>;
  ongoingCommissionTermsUpfrontFee: Nullable<string>;
  ongoingCommissionTermsOngoingFee: Nullable<string>;
  clubName: Nullable<string>;
  submissionRoute: Nullable<string>;
  commissionPaidTo: Nullable<string>;
  networkName: Nullable<string>;
  firmName: Nullable<string>;
  firmFcaNumber: Nullable<string>;
  firmAddress: Nullable<string>;
  networkFcaNumber: Nullable<string>;
  adviserFeeLastPaymentMoment: Nullable<string>;
  packagerFee: Nullable<number>;
  packagerFeeLastPaymentMoment: Nullable<string>;
  upfrontCommissionAmount: Nullable<number>;
  ongoingCommissionAmount: Nullable<number>;
  property: Property;
  hasMarketingEmail: Nullable<boolean>;
  hasMarketingSms: Nullable<boolean>;
  hasMarketingPhone: Nullable<boolean>;
  hasMarketingPost: Nullable<boolean>;
  fcaLoanPurpose: Nullable<string>;
  outstandingMortgageAmount: Nullable<number>;
  sourceOfFundsToClearOutstandingMortgage: Nullable<string>;
  existingMortgageStatus: Nullable<string>;
  additionalDetails: Nullable<string>;
  annualCouncilTax: Nullable<number>;
  annualGroundRent: Nullable<number>;
  loanOtherPurposes: Nullable<number>;
  rationale: string;
  offerPackUrl: string;
  preOfferEsisUrl: string;
  isPepperUploadApproved: boolean;
  repaymentStrategy: RepaymentStrategy;
} & { policies: AssurancePolicy[] } & Conveyancing &
  Admin &
  Underwriting &
  AffordabilityResults &
  Product;

export type ResourcesData = {
  gender: Resource[];
  applicationType: Resource[];
  caseStatus: Resource[];
  creditPolicyExemption: Resource[];
  loanPurposePepper: Resource[];
  pepperProductName: Resource[];
  productFeePaymentMethod: Resource[];
  purposeOfTheLoan: Resource[];
  sourceOfDeposit: Resource[];
  valuationFee: Resource[];
  underwriters: Resource[];
  creditFeePayMoment: Resource[];
  originatorIds: Resource[];
  assetTypes: Resource[];
  title: Resource[];
  proofOfId: Resource[];
  proofOfAddress: Resource[];
  martialStatus: Resource[];
  products: Resource[];
  nationalities: Resource[];
  expenditurePaymentFrequencies: Resource[];
  expenditureTypes: Resource[];
  useOfFund: Resource[];
  employmentStatuses: Resource[];
  employmentNatures: Resource[];
  businessTypes: Resource[];
  propertyTypes: Resource[];
  tenureTypes: Resource[];
  propertyConstructions: Resource[];
  valuationTypes: Resource[];
  assuredPersons: Resource[];
  policyTypes: Resource[];
  basisCovers: Resource[];
  fcaLoanPurposes: Resource[];
  existingMortgageStatuses: Resource[];
  authorityLevels: Resource[];
  approvalLevels: Resource[];
  vulnerabilityCategories: Resource[];
  informationProvidedBy: Resource[];
  productTermLengths: Resource[];
  tiers: Resource[];
};

export type CaseResponseData = {
  case: CaseData;
  dueDiligenceInfo: {
    ddStatus: Nullable<DueDiligenceStatus>;
  };
  resources: ResourcesData;
  underwriting: UnderwritingSummary;
  pepperReview: PepperReviewSummary;
  products: APIProduct[];
  error: Nullable<DomainCaseError>;
};

export interface CaseErrorsResponseData extends Omit<CaseResponseData, 'error'> {
  error: APICaseError;
}
export interface GetCaseResponseData extends CaseErrorsResponseData {}
export interface PatchCaseResponseData extends CaseErrorsResponseData {}
export interface PatchApplicantResponseData extends CaseErrorsResponseData {}
export interface PatchApplicantAddressResponseData extends CaseErrorsResponseData {}
export interface PatchPowerOfAttorneyResponseData extends CaseErrorsResponseData {}
export interface PatchAssetResponseData extends CaseErrorsResponseData {}
export interface PatchExpenditureResponseData extends CaseErrorsResponseData {}
export interface PatchDefinedBenefitResponseData extends CaseErrorsResponseData {}
export interface PatchDefinedContributionResponseData extends CaseErrorsResponseData {}
export interface PatchFutureAnnuityResponseData extends CaseErrorsResponseData {}
export interface PatchPropertyResponseData extends CaseErrorsResponseData {}
export interface PatchPropertyOccupantResponseData extends CaseErrorsResponseData {}
export interface PatchApplicantIncomeResponseData extends CaseErrorsResponseData {}
export interface PatchPolicyResponseData extends CaseErrorsResponseData {}

export type CaseId = string;
export type ApplicantId = string;
export const API_PREFIX = '/api/v1/cases';
